import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getScholarshipProofRequestByToken, submitScholarshipProofByToken } from '../../../services/crmApi';
import { uploadFilePublic } from '../../../services/fileApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import ProofAttachmentDropzone from '../../Common/ProofAttachmentDropzone';
import { getSlimLogoPath } from '../../../utils/slimLogo';

/**
 * Public page opened from the e-mail link staff send when a scholarship proof / agreement is missing or rejected.
 * Route: /scholarship-proof/:token
 */
const PublicScholarshipProofUpload = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getScholarshipProofRequestByToken(token);
        if (!cancelled) setInfo(res.data?.data || null);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || 'This upload link is invalid or has expired.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Please choose the scholarship proof / agreement document');
      return;
    }
    setSubmitting(true);
    try {
      const upload = await uploadFilePublic(file, {
        category: 'Other',
        entityType: 'Application',
        entityId: info.applicationId,
        title: 'Scholarship Proof / Agreement',
        description: 'Scholarship proof / agreement document (upload link)'
      });
      const uploaded = upload.data?.data || {};
      const fileId = uploaded.fileId || uploaded.fileID || uploaded._id;
      if (!upload.data?.success || !fileId) throw new Error('Upload failed');
      await submitScholarshipProofByToken(token, {
        fileId,
        fileName: uploaded.originalName || uploaded.fileName || file.name,
        fileSize: uploaded.fileSize || file.size,
        mimeType: uploaded.mimeType || file.type
      });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Could not submit the document. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center px-4 py-10">
      <div className="w-full max-w-lg bg-white rounded-xl shadow border border-gray-200 p-6">
        <div className="flex justify-center mb-4">
          <img src={getSlimLogoPath()} alt="Logo" className="w-16 h-16 object-contain" />
        </div>
        {children}
      </div>
    </div>
  );

  if (loading) return shell(<div className="py-10 flex justify-center"><LoadingSpinner /></div>);

  if (error || !info) {
    return shell(
      <div className="text-center" data-testid="scholarship-proof-link-error">
        <h1 className="text-lg font-semibold text-gray-900 mb-2">Link unavailable</h1>
        <p className="text-sm text-gray-600">{error || 'This upload link is invalid or has expired.'}</p>
      </div>
    );
  }

  if (done) {
    return shell(
      <div className="text-center" data-testid="scholarship-proof-done">
        <h1 className="text-lg font-semibold text-emerald-700 mb-2">Thank you</h1>
        <p className="text-sm text-gray-600">
          Your scholarship document for application <strong>{info.applicationNumber}</strong> was received.
          Our team will review it and contact you if anything else is needed.
        </p>
      </div>
    );
  }

  return shell(
    <div data-testid="scholarship-proof-upload">
      <h1 className="text-lg font-semibold text-gray-900 text-center">Upload scholarship proof / agreement</h1>
      <div className="mt-4 text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-emerald-900 space-y-0.5">
        {info.applicantName && <div>Applicant: <strong>{info.applicantName}</strong></div>}
        <div>Application: <strong>{info.applicationNumber}</strong></div>
        {info.programName && <div>Programme: <strong>{info.programName}</strong></div>}
        <div>Scholarship: <strong>{info.schemeName}</strong></div>
      </div>

      {info.proofStatus === 'Rejected' && (
        <div className="mt-3 text-sm bg-red-50 border border-red-200 rounded-lg p-3 text-red-800">
          Your previous document was not accepted{info.rejectionNote ? `: ${info.rejectionNote}` : '.'} Please upload a new one.
        </div>
      )}

      {info.canUpload ? (
        <div className="mt-4 space-y-4">
          <ProofAttachmentDropzone
            file={file}
            onChange={setFile}
            required
            busy={submitting}
            label="Scholarship document"
            hint={null}
          />
          <button
            type="button"
            disabled={submitting || !file}
            onClick={handleSubmit}
            data-testid="scholarship-proof-submit"
            className="w-full py-2.5 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : 'Submit document'}
          </button>
        </div>
      ) : (
        <p className="mt-4 text-sm text-gray-700" data-testid="scholarship-proof-no-upload">
          {info.proofStatus === 'Approved'
            ? 'Your scholarship document has already been approved. No further action is needed.'
            : 'Your scholarship document has been received and is awaiting review.'}
        </p>
      )}
    </div>
  );
};

export default PublicScholarshipProofUpload;
